/**
 * Content Ideas Dialog Component
 * 
 * Displays content ideas for a specific bucket to inspire users with
 * relevant content suggestions based on their industry and bucket type.
 * 
 * Features:
 * - Display curated content ideas
 * - Filter by difficulty level
 * - Copy ideas to clipboard
 * - Mobile-responsive design
 * 
 * @author AI Assistant
 * @created 2024
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Lightbulb as IdeaIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { useContentBuckets } from '../providers/ContentBucketsProvider';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 500,
    maxWidth: 700,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      maxWidth: '95vw',
      margin: theme.spacing(1)
    }
  }
}));

const IdeaCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
    '& .copy-button': {
      opacity: 1
    }
  },
  '& .copy-button': {
    opacity: 0,
    transition: 'opacity 0.2s ease'
  }
}));

const DifficultyChip = styled(Chip)(({ difficulty, theme }) => {
  const colors = {
    beginner: theme.palette.success.main,
    intermediate: theme.palette.warning.main,
    advanced: theme.palette.error.main
  };
  
  return {
    backgroundColor: colors[difficulty] || theme.palette.grey[300],
    color: theme.palette.common.white,
    fontWeight: 500
  };
});

const ContentIdeasDialog = ({ open, onClose, bucket }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { fetchBucketIdeas, loading, error } = useContentBuckets();

  // Local state
  const [ideas, setIdeas] = useState([]);
  const [filteredIdeas, setFilteredIdeas] = useState([]);
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch ideas when dialog opens
  useEffect(() => {
    if (open && bucket) {
      fetchIdeas();
    }
  }, [open, bucket]);

  // Filter ideas when filter changes
  useEffect(() => {
    if (difficultyFilter === 'all') {
      setFilteredIdeas(ideas);
    } else {
      setFilteredIdeas(ideas.filter(idea => idea.difficulty_level === difficultyFilter));
    }
  }, [ideas, difficultyFilter]);

  const fetchIdeas = async () => {
    try {
      const fetchedIdeas = await fetchBucketIdeas(bucket.id);
      setIdeas(fetchedIdeas || []);
    } catch (err) {
      console.error('Failed to fetch content ideas:', err);
    }
  };

  // Handle copy to clipboard
  const handleCopyIdea = async (ideaText) => {
    try {
      await navigator.clipboard.writeText(ideaText);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  // Get difficulty icon
  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return <StarIcon />;
      case 'intermediate':
        return (
          <>
            <StarIcon />
            <StarIcon />
          </>
        );
      case 'advanced':
        return (
          <>
            <StarIcon />
            <StarIcon />
            <StarIcon />
          </>
        );
      default:
        return <StarBorderIcon />;
    }
  };

  // Format difficulty text
  const formatDifficulty = (difficulty) => {
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <IdeaIcon color="primary" />
            <Typography variant="h6" component="h2">
              Content Ideas for "{bucket?.name}"
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Filter Controls */}
          <Box sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
                label="Difficulty"
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Loading State */}
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}

          {/* Ideas List */}
          {!loading && filteredIdeas.length > 0 && (
            <Box>
              {filteredIdeas.map((idea, index) => (
                <IdeaCard key={index} elevation={1}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box flex={1}>
                        <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.6 }}>
                          {idea.idea_text}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DifficultyChip
                            difficulty={idea.difficulty_level}
                            label={formatDifficulty(idea.difficulty_level)}
                            size="small"
                          />
                          <Box display="flex" alignItems="center" color="text.secondary">
                            {getDifficultyIcon(idea.difficulty_level)}
                          </Box>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyIdea(idea.idea_text)}
                        className="copy-button"
                        sx={{ ml: 1 }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </IdeaCard>
              ))}
            </Box>
          )}

          {/* Empty State */}
          {!loading && filteredIdeas.length === 0 && ideas.length > 0 && (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              minHeight="200px"
              textAlign="center"
            >
              <IdeaIcon sx={{ fontSize: '3rem', color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No ideas match your filter
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try selecting a different difficulty level
              </Typography>
            </Box>
          )}

          {/* No Ideas Available */}
          {!loading && ideas.length === 0 && (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              minHeight="200px"
              textAlign="center"
            >
              <IdeaIcon sx={{ fontSize: '3rem', color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No content ideas available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Content ideas for this bucket type are coming soon!
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Idea copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ContentIdeasDialog; 